/**
 * Pooja — pricing tiles + Razorpay checkout entry.
 *
 * Design tokens from pooja/index.html:
 *   --ink     #1a1208  --saffron #c5460e  --gold   #b6850f
 *   --cream   #fdf6e7  --cream-2 #f5e9c8  --ash    #a8a39a
 *   --bg      #fdf6e7  --rule    #e3c98a
 *   Font: Crimson Pro + Mukta
 */
import { PRICING, openCheckout } from '../chassis/razorpay.ts';

const TPL = /* html */ `
<style>
:host {
  display: block;
  margin: 48px 0 32px;
}
.shell {
  border: 1px solid #e3c98a;
  background: linear-gradient(180deg, #fff8e1 0%, #fdf6e7 100%);
  padding: 40px 28px 28px;
  border-radius: 6px;
  box-shadow: 0 1px 0 #fff inset, 0 8px 24px rgba(197,70,14,.06);
}
.head {
  text-align: center;
  margin-bottom: 32px;
}
.eyebrow {
  font-family: 'Mukta', sans-serif;
  font-size: 13px;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: #b6850f;
  font-weight: 600;
}
h3 {
  font-family: 'Crimson Pro', Georgia, serif;
  font-size: 36px;
  font-style: italic;
  font-weight: 700;
  margin: 8px 0 6px;
  color: #1a1208;
  line-height: 1.1;
}
.tagline {
  font-family: 'Crimson Pro', Georgia, serif;
  font-size: 18px;
  color: #5a4422;
  font-style: italic;
  line-height: 1.4;
}
.tiers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 20px;
}
.tier {
  background: #fff;
  border: 1px solid #e3c98a;
  border-radius: 6px;
  padding: 24px 18px;
  display: grid;
  gap: 10px;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;
}
.tier:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(197,70,14,.10); }
.tier.recommended { border-color: #c5460e; border-width: 2px; }
.tier.recommended::before {
  content: 'recommended';
  display: block;
  font-family: 'Mukta', sans-serif;
  font-size: 11px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #fdf6e7;
  background: #c5460e;
  border-radius: 4px 4px 0 0;
  padding: 4px 8px;
  margin: -24px -18px 10px;
  text-align: center;
}
.tier .label {
  font-family: 'Mukta', sans-serif;
  font-size: 12px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #a8a39a;
  font-weight: 600;
}
.tier .price {
  font-family: 'Crimson Pro', Georgia, serif;
  font-size: 38px;
  font-style: italic;
  color: #c5460e;
  font-weight: 700;
  line-height: 1;
}
.tier .per {
  font-family: 'Mukta', sans-serif;
  font-size: 12px;
  color: #a8a39a;
  letter-spacing: 0.08em;
}
.tier .desc {
  font-family: 'Mukta', sans-serif;
  font-size: 14px;
  color: #4a3322;
  line-height: 1.5;
}
.tier button {
  appearance: none;
  font-family: 'Mukta', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 12px 10px;
  min-height: 44px;
  background: #1a1208;
  color: #fdf6e7;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 6px;
  transition: background 100ms;
}
.tier button:hover { background: #3a2a14; }
.tier.recommended button { background: #c5460e; }
.tier.recommended button:hover { background: #9a3306; }
.footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px dashed #e3c98a;
  font-family: 'Mukta', sans-serif;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-align: center;
  color: #a8a39a;
}
.success {
  margin-top: 18px;
  padding: 14px;
  background: rgba(182,133,15,.10);
  border-left: 4px solid #b6850f;
  font-family: 'Mukta', sans-serif;
  font-size: 15px;
  color: #1a1208;
  display: none;
  border-radius: 0 4px 4px 0;
}
.success.show { display: block; }
</style>
<section class="shell">
  <div class="head">
    <div class="eyebrow">Mulya · Pricing · MMXXVI</div>
    <h3>Aapke ghar ka digital pujari. Hamesha tayyar.</h3>
    <div class="tagline">Daily panchang · priest booking · mantra coach · 80G donation receipts — sab ek jagah.</div>
  </div>
  <div class="tiers">
    <div class="tier" data-tier="free">
      <div class="label">Free</div>
      <div class="price">₹0</div>
      <div class="per">hamesha ke liye</div>
      <div class="desc">Daily panchang · festival calendar · 108-mantra catalogue · basic temple finder.</div>
      <button data-go="free">Start free</button>
    </div>
    <div class="tier recommended" data-tier="pro_yearly">
      <div class="label">Pro · Yearly</div>
      <div class="price">₹499</div>
      <div class="per">per year</div>
      <div class="desc">Priest booking · voice mantra guide · ad-free Pooja · 80G receipt export · panchang API.</div>
      <button data-go="pro_yearly">Pro khareedein · ₹499</button>
    </div>
    <div class="tier" data-tier="family_yearly">
      <div class="label">Family · Yearly</div>
      <div class="price">₹2,999</div>
      <div class="per">per year · 6 members</div>
      <div class="desc">6 family members · joint puja calendar · offline ritual guide · priority priest booking.</div>
      <button data-go="family_yearly">Family khareedein · ₹2,999</button>
    </div>
    <div class="tier" data-tier="bundle_yearly">
      <div class="label">Empire Bundle</div>
      <div class="price">₹2,999</div>
      <div class="per">all 8 apps · 1 year</div>
      <div class="desc">Pooja + Hisaab + Sehat + Sarathi + Bachcha + sab — ek subscription mein.</div>
      <button data-go="bundle_yearly">Bundle khareedein · ₹2,999</button>
    </div>
  </div>
  <div class="success" id="success"></div>
  <div class="footer">
    Razorpay live · 30-day money back · GST e-invoice · DPDP 2023 compliant · 80G donations separate at /donate
  </div>
</section>
`;

export class PoojaPayCta extends HTMLElement {
  readonly #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const success = this.#shadow.getElementById('success') as HTMLDivElement;
    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-go]').forEach((b) => {
      b.addEventListener('click', async () => {
        const tier = b.dataset.go;
        if (tier === 'free' || typeof tier !== 'string') return;
        if (tier !== 'pro_yearly' && tier !== 'family_yearly' && tier !== 'bundle_yearly') return;
        try {
          await openCheckout(tier, {}, (pid) => {
            success.textContent = `Payment received · Razorpay id ${pid}. Pooja Pro access is on the way. Om Shri Ganeshaya Namah.`;
            success.classList.add('show');
            localStorage.setItem('pooja:tier', tier);
            localStorage.setItem('pooja:payment_id', pid);
          });
        } catch (e) {
          success.textContent = `Checkout failed · ${e instanceof Error ? e.message : 'unknown'}. Email aditya@taxwalaai.com.`;
          success.classList.add('show');
        }
      });
    });
    void PRICING; // referenced for typecheck
  }
}

customElements.define('pooja-pay-cta', PoojaPayCta);
